import React, { useState } from "react";
import "./MakePayment.css";
import { API_BASE_URL, LOGOUT_URL } from "./config";
import { useLoading } from "./LoadingContext";

export default function MakePayment({ paymentDetails, onPaymentSuccess }) {
  const { showLoading, hideLoading } = useLoading();
  const [paymentStarted, setPaymentStarted] = useState(false);

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  if (!paymentDetails) {
    return null;
  }

  const studentId = paymentDetails.studentId;

  const paymentContext =
    paymentDetails.paymentContext || "TRAINING_INSTALLMENT";

  const planType =
    paymentDetails.planType || "INSTALLMENT";

  const installmentNumbersToPay =
    paymentDetails.installmentNumbersToPay ||
    paymentDetails.installment_numbers_to_pay ||
    paymentDetails.missingInstallmentNumbers ||
    paymentDetails.missing_installment_numbers ||
    [];

  const askStudentFee = Number(paymentDetails.askStudentFee || 0);
  const gstPercent = Number(paymentDetails.gstPercent || 18);

  const gstAmount = Number(
    paymentDetails.gstAmount !== undefined
      ? paymentDetails.gstAmount
      : (askStudentFee * gstPercent) / 100
  );

  const totalPayable = Number(
    paymentDetails.totalAmount !== undefined
      ? paymentDetails.totalAmount
      : askStudentFee + gstAmount
  );

  const paidInstallmentCount = paymentDetails.paidInstallmentCount || 0;
  const requiredInstallmentCount = paymentDetails.requiredInstallmentCount || 0;
  const pendingInstallmentCount = paymentDetails.pendingInstallmentCount || 0;
  const studentDay = paymentDetails.studentDay || 1;

  const logoutUrl = LOGOUT_URL;

  const handleInstallmentPayment = async () => {
    if (!studentId) {
      alert("Student ID missing. Please login again.");
      window.location.href = logoutUrl;
      return;
    }

    if (!Array.isArray(installmentNumbersToPay) || installmentNumbersToPay.length === 0) {
      alert("Pending installment numbers missing. Please login again.");
      window.location.href = logoutUrl;
      return;
    }

    try {
      setPaymentStarted(true);
      showLoading("Preparing your pending installment payment...");

      const orderResponse = await fetch(`${API_BASE_URL}/payment/create-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          student_id: studentId,
          payment_context: paymentContext,
          plan_type: planType,
          installment_numbers: installmentNumbersToPay,
        }),
      });

      const orderData = await orderResponse.json();

      if (!orderResponse.ok || !orderData.order_id) {
        alert(orderData.detail || "Unable to create payment order.");
        return;
      }

      await sleep(500);
      hideLoading();

      const options = {
        key: orderData.key_id,
        amount: Math.round(Number(orderData.total_amount) * 100),
        currency: orderData.currency || "INR",
        name: "StudyAIPlacement",
        description: "Pending Installment Payment",
        order_id: orderData.order_id,

        handler: async function (response) {
          try {
            showLoading("Verifying your installment payment...");

            const verifyResponse = await fetch(`${API_BASE_URL}/payment/verify`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                razorpay_order_id: String(response.razorpay_order_id),
                razorpay_payment_id: String(response.razorpay_payment_id),
                razorpay_signature: String(response.razorpay_signature),
              }),
            });

            const verifyData = await verifyResponse.json();

            if (!verifyResponse.ok) {
              alert(
                verifyData.detail ||
                  "Payment verification failed. Please contact support."
              );
              return;
            }

            alert(
              "✅ Installment payment successful. Please login again to access your dashboard."
            );

            setPaymentStarted(false);

            localStorage.removeItem("student_unique_id");
            localStorage.removeItem("token");

            if (typeof onPaymentSuccess === "function") {
              onPaymentSuccess();
            } else {
              window.location.reload();
            }
          } catch (error) {
            console.error("Installment verify error:", error);
            alert("Payment verification failed. Please contact support.");
          } finally {
            await sleep(500);
            hideLoading();
            document.body.classList.remove("razorpay-active");
          }
        },

        modal: {
          escape: false,
          backdropclose: false,
          ondismiss: function () {
            setPaymentStarted(false);
            document.body.classList.remove("razorpay-active");
            alert("Payment was not completed. Login access will remain blocked.");
          },
        },

        theme: {
          color: "#3399cc",
        },
      };

      const rzp = new window.Razorpay(options);
      document.body.classList.add("razorpay-active");
      rzp.open();
    } catch (error) {
      console.error("Pending installment payment error:", error);
      alert("Unable to start payment. Please try again.");
    } finally {
      await sleep(500);
      hideLoading();
      setPaymentStarted(false);
    }
  };

  return (
    <div className="make-payment-page">
      <div className="make-payment-overlay"></div>

      <div className="make-payment-shell">
        <div className="make-payment-left">
          <div className="make-payment-brand">
            <img src="/sneh-logo.png" alt="StudyAIPlacement" />
            <span>Student Payment Gate</span>
          </div>

          <h1>Pending installment required to continue learning.</h1>

          <p>
            Your student account is verified, but your installment payment is not
            sufficient for the current course day. Complete the pending
            installment to continue dashboard access.
          </p>

          <div className="make-payment-status-grid">
            <div>
              <small>Current Course Day</small>
              <strong>Day {studentDay}</strong>
            </div>

            <div>
              <small>Paid Installments</small>
              <strong>{paidInstallmentCount}</strong>
            </div>

            <div>
              <small>Required Installments</small>
              <strong>{requiredInstallmentCount}</strong>
            </div>

            <div>
              <small>Pending Installments</small>
              <strong>{pendingInstallmentCount}</strong>
            </div>
          </div>
        </div>

        <div className="make-payment-card">
          <div className="make-payment-card-head">
            <span>PAYMENT REQUIRED</span>
            <h2>Pending Installment</h2>
            <p>
              Pay the required installment amount and then login again to access
              your dashboard.
            </p>
          </div>

          <div className="make-payment-summary">
            <div>
              <span>Installments To Pay</span>
              <b>
                {installmentNumbersToPay.length > 0
                  ? installmentNumbersToPay.join(", ")
                  : "-"}
              </b>
            </div>

            <div>
              <span>Pending Base Amount</span>
              <b>₹{askStudentFee.toFixed(2)}</b>
            </div>

            <div>
              <span>GST ({gstPercent}%)</span>
              <b>₹{gstAmount.toFixed(2)}</b>
            </div>

            <div className="make-payment-total">
              <span>Total Payable</span>
              <b>₹{totalPayable.toFixed(2)}</b>
            </div>
          </div>

          <div className="make-payment-note">
            <strong>Important:</strong>
            <p>
              This payment will be recorded against exact installment number(s):{" "}
              <b>
                {installmentNumbersToPay.length > 0
                  ? installmentNumbersToPay.join(", ")
                  : "-"}
              </b>
              . Your access will open after successful payment verification.
            </p>
          </div>

          <button
            className="make-payment-btn"
            type="button"
            disabled={paymentStarted}
            onClick={handleInstallmentPayment}
          >
            {paymentStarted
              ? "Starting Payment..."
              : `Pay ₹${totalPayable.toFixed(2)} Now`}
          </button>

          <button
            className="make-payment-secondary-btn"
            type="button"
            onClick={() => {
              localStorage.removeItem("student_unique_id");
              localStorage.removeItem("token");
              window.location.href = logoutUrl;
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
}